import React, { useState, useEffect } from 'react';
import { X, Flame, Zap, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import { PublicKey, Transaction } from '@solana/web3.js';
import tokenBurnService from '../../services/tokenBurnService';
import { auth } from '../../firebase';
import { walletService } from '../../utils/wallet';
import logger from '../../utils/logger';

const BurnForBoostModal = ({ isOpen, onClose }) => {
  // Use the custom wallet service instead of wallet adapter hooks
  const walletInfo = walletService.getWalletInfo();
  const publicKey = walletInfo.isConnected ? new PublicKey(walletInfo.address) : null;
  const connected = walletInfo.isConnected;
  const signTransaction = walletService.wallet?.signTransaction?.bind(walletService.wallet);
  const [selectedBoost, setSelectedBoost] = useState(null);
  const [burning, setBurning] = useState(false);
  const [burnSuccess, setBurnSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [tokenBalance, setTokenBalance] = useState(0);
  const [activeBoosts, setActiveBoosts] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState({});

  const boostOptions = [
    {
      id: 'xp_2x_1hr',
      name: '2x XP Multiplier',
      duration: '1 hour',
      cost: 10000,
      icon: '⚡',
      color: 'from-yellow-500 to-orange-500',
      description: 'Double your XP earnings for 1 hour (testing)'
    },
    {
      id: 'xp_3x_1hr',
      name: '3x XP Multiplier',
      duration: '1 hour',
      cost: 250000,
      icon: '🔥',
      color: 'from-orange-500 to-red-500',
      description: 'Triple your XP earnings for 1 hour'
    },
    {
      id: 'xp_5x_1hr',
      name: '5x XP Multiplier',
      duration: '1 hour',
      cost: 1000000,
      icon: '🚀',
      color: 'from-purple-500 to-pink-500',
      description: 'Massive 5x XP boost for 1 hour'
    },
    {
      id: 'refresh_prediction_limit',
      name: 'Refresh Prediction Limit',
      duration: 'Instant',
      cost: 10000,
      icon: '🔄',
      color: 'from-blue-500 to-cyan-500',
      description: 'Just reset your daily prediction limit'
    }
  ];

  useEffect(() => {
    if (connected && publicKey && auth.currentUser) {
      checkTokenBalance();
      loadActiveBoosts();
    }
  }, [connected, publicKey]);

  // Update countdown timers every second
  useEffect(() => {
    if (!isOpen || activeBoosts.length === 0) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const newTimeRemaining = {};

      activeBoosts.forEach(boost => {
        if (boost.endTime > now) {
          const remaining = boost.endTime - now;
          const minutes = Math.floor(remaining / 60000);
          const seconds = Math.floor((remaining % 60000) / 1000);
          // Use boost.type as the key to match the boost.id in UI
          newTimeRemaining[boost.type] = `${minutes}m ${seconds}s`;
        }
      });

      setTimeRemaining(newTimeRemaining);
    }, 1000);

    // Also run immediately on mount
    const now = Date.now();
    const initialTimeRemaining = {};
    activeBoosts.forEach(boost => {
      if (boost.endTime > now) {
        const remaining = boost.endTime - now;
        const minutes = Math.floor(remaining / 60000);
        const seconds = Math.floor((remaining % 60000) / 1000);
        initialTimeRemaining[boost.type] = `${minutes}m ${seconds}s`;
      }
    });
    setTimeRemaining(initialTimeRemaining);

    return () => clearInterval(interval);
  }, [isOpen, activeBoosts]);

  const loadActiveBoosts = async () => {
    try {
      const { getDoc, doc } = await import('firebase/firestore');
      const { db } = await import('../../firebase');

      const userBoostsRef = doc(db, 'userBoosts', auth.currentUser.uid);
      const boostsDoc = await getDoc(userBoostsRef);

      if (boostsDoc.exists()) {
        const boosts = boostsDoc.data().activeBoosts || [];
        const now = Date.now();

        // Filter only active boosts that haven't expired
        const active = boosts.filter(boost =>
          boost.status === 'active' && boost.endTime > now
        );

        setActiveBoosts(active);
      }
    } catch (error) {
      logger.error('Error loading active boosts:', error);
    }
  };

  const checkTokenBalance = async () => {
    try {
      const { getAssociatedTokenAddress } = await import('@solana/spl-token');
      const { PublicKey } = await import('@solana/web3.js');

      const tokenMint = new PublicKey(tokenBurnService.TOPBLAST_CA);
      const walletPubkey = new PublicKey(publicKey.toString());

      // Get associated token account
      const tokenAccount = await getAssociatedTokenAddress(tokenMint, walletPubkey);

      // Get balance
      const balance = await tokenBurnService.connection.getTokenAccountBalance(tokenAccount);
      const tokenBalance = balance.value.uiAmount || 0;

      logger.log('💰 Test token balance:', tokenBalance);
      setTokenBalance(tokenBalance);
    } catch (error) {
      logger.error('Error checking balance:', error);
      setTokenBalance(0);
    }
  };

  const handleBurn = async () => {
    if (!selectedBoost || !connected || !publicKey) return;

    // Check if trying to activate an XP boost while one is already active
    const isXPBoost = selectedBoost.id.startsWith('xp_');
    const hasActiveXPBoost = activeBoosts.some(boost => boost.type.startsWith('xp_'));

    if (isXPBoost && hasActiveXPBoost) {
      setError('You already have an active XP boost! Wait for it to expire before activating another.');
      return;
    }

    setBurning(true);
    setError(null);

    try {
      logger.log('🔥 Starting burn for:', selectedBoost.name);

      // Create and send burn transaction using the service
      const burnResult = await tokenBurnService.createBurnTransaction(
        publicKey.toString(),
        selectedBoost.cost
      );

      logger.log('✅ Burn transaction confirmed:', burnResult.signature);

      // Process burn in Firebase
      const result = await tokenBurnService.processBurn(
        auth.currentUser.uid,
        selectedBoost.id,
        burnResult.signature,
        selectedBoost.cost
      );

      if (result.success) {
        logger.log('🎉 Burn processed successfully! Boost applied:', selectedBoost.id);

        // Show specific success message for prediction limit refresh
        if (selectedBoost.id === 'refresh_prediction_limit') {
          logger.log('✅ Daily prediction limit has been reset!');
        }

        // Reload active boosts to show the new one
        await loadActiveBoosts();

        setBurnSuccess(true);
        setTimeout(() => {
          onClose();
          setBurnSuccess(false);
          setSelectedBoost(null);
        }, 3000);
      }
    } catch (error) {
      logger.error('Burn failed:', error);
      setError(error.message || 'Failed to burn tokens');
    } finally {
      setBurning(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Flame className="w-8 h-8 text-orange-500" />
              <div>
                <h2 className="text-2xl font-bold text-white">Burn for Boosts</h2>
                <p className="text-gray-400 text-sm">Burn TOPBLAST tokens to unlock powerful boosts</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Balance Display */}
        <div className="p-4 bg-gray-800/50 m-6 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Your Balance:</span>
            <span className="text-xl font-bold text-white">{tokenBalance.toLocaleString()} TOPBLAST</span>
          </div>
        </div>

        {/* Boost Options */}
        <div className="p-6 space-y-4">
          {boostOptions.map((boost) => {
            const isXPBoost = boost.id.startsWith('xp_');
            const activeBoost = activeBoosts.find(b => b.type === boost.id);
            const isActive = !!activeBoost;
            const hasActiveXPBoost = activeBoosts.some(b => b.type.startsWith('xp_'));
            const isDisabled = tokenBalance < boost.cost || (isXPBoost && hasActiveXPBoost && !isActive);

            return (
              <button
                key={boost.id}
                onClick={() => !isDisabled && setSelectedBoost(boost)}
                disabled={isDisabled}
                className={`w-full p-4 rounded-xl border-2 transition-all ${
                  selectedBoost?.id === boost.id
                    ? 'border-purple-500 bg-purple-500/10'
                    : isActive
                    ? 'border-green-500 bg-green-500/10'
                    : 'border-gray-700 hover:border-gray-600 bg-gray-800/50'
                } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`text-3xl p-3 rounded-lg bg-gradient-to-r ${boost.color} bg-opacity-20`}>
                      {boost.icon}
                    </div>
                    <div className="text-left">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-bold text-white">{boost.name}</h3>
                        {isActive && (
                          <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full font-semibold">
                            ACTIVE
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-400">{boost.description}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Clock className="w-4 h-4 text-gray-500" />
                        {isActive && timeRemaining[boost.id] ? (
                          <span className="text-sm font-semibold text-green-400">
                            {timeRemaining[boost.id]} remaining
                          </span>
                        ) : (
                          <span className="text-sm text-gray-500">Duration: {boost.duration}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-orange-400">{boost.cost.toLocaleString()}</div>
                    <div className="text-xs text-gray-500">TOPBLAST</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mx-6 p-4 bg-red-500/10 border border-red-500/50 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-400">{error}</span>
          </div>
        )}

        {/* Success Message */}
        {burnSuccess && (
          <div className="mx-6 p-4 bg-green-500/10 border border-green-500/50 rounded-lg flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-green-400">Boost activated successfully! 🚀</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="p-6 border-t border-gray-800">
          {!connected ? (
            <div className="text-center">
              <p className="text-yellow-400 mb-4">Please connect your wallet to burn tokens</p>
            </div>
          ) : (
            <div className="flex gap-4">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-gray-800 text-gray-300 rounded-xl hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBurn}
                disabled={!selectedBoost || burning || tokenBalance < selectedBoost?.cost}
                className={`flex-1 px-6 py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
                  selectedBoost && !burning && tokenBalance >= selectedBoost.cost
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600'
                    : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                }`}
              >
                {burning ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Burning...
                  </>
                ) : (
                  <>
                    <Flame className="w-5 h-5" />
                    Burn {selectedBoost?.cost || 0} TOPBLAST
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Warning */}
        <div className="p-4 bg-yellow-500/10 border-t border-yellow-500/50 text-center">
          <p className="text-yellow-400 text-sm">
            ⚠️ Burning is permanent! Tokens will be destroyed forever, reducing total supply.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BurnForBoostModal;